import { ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent, memo, useCallback, useRef } from 'react';
import { alpha, Box } from '@mui/material';

type CodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');

/**
 * Segmented code entry. The value is always a compact digit string (no gaps), so
 * consumers can validate it with a plain `value.length === length` check.
 */
const CodeInput = ({ value, onChange, length = 6, disabled, error, autoFocus, ariaLabel }: CodeInputProps) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = onlyDigits(value).slice(0, length);

  const focusAt = useCallback(
    (index: number) => {
      const target = inputsRef.current[Math.min(Math.max(index, 0), length - 1)];
      target?.focus();
      target?.select();
    },
    [length],
  );

  const handleChange = useCallback(
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const typed = onlyDigits(event.target.value);

      if (!typed) {
        onChange(code.slice(0, index) + code.slice(index + 1));
        return;
      }

      onChange((code.slice(0, index) + typed + code.slice(index + typed.length)).slice(0, length));
      focusAt(index + typed.length);
    },
    [code, focusAt, length, onChange],
  );

  const handleKeyDown = useCallback(
    (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace') {
        event.preventDefault();
        // Backspace on an empty box steps back and clears the previous digit.
        const target = code[index] ? index : index - 1;
        if (target < 0) return;
        onChange(code.slice(0, target) + code.slice(target + 1));
        focusAt(target);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        focusAt(index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        focusAt(index + 1);
      }
    },
    [code, focusAt, onChange],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const pasted = onlyDigits(event.clipboardData.getData('text')).slice(0, length);
      if (!pasted) return;
      event.preventDefault();
      onChange(pasted);
      focusAt(pasted.length);
    },
    [focusAt, length, onChange],
  );

  return (
    <Box role='group' aria-label={ariaLabel} sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.25 }, justifyContent: 'center' }}>
      {Array.from({ length }).map((_, index) => (
        <Box
          // Boxes are positional and never reordered, so the index is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          component='input'
          ref={(element: HTMLInputElement | null) => {
            inputsRef.current[index] = element;
          }}
          type='text'
          inputMode='numeric'
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`${ariaLabel || ''} ${index + 1}`.trim()}
          maxLength={1}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          value={code[index] || ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={(event: FocusEvent<HTMLInputElement>) => { event.target.select(); }}
          sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 48, sm: 56 },
            textAlign: 'center',
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 600,
            fontFamily: 'inherit',
            color: 'text.primary',
            bgcolor: 'background.paper',
            border: '1.5px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1.5,
            outline: 'none',
            transition: 'border-color .15s, box-shadow .15s',
            '&:hover:not(:disabled)': { borderColor: error ? 'error.main' : 'text.disabled' },
            '&:focus': {
              borderColor: error ? 'error.main' : 'primary.main',
              boxShadow: ({ palette }) => `0 0 0 3px ${alpha(error ? palette.error.main : palette.primary.main, 0.2)}`,
            },
            '&:disabled': { bgcolor: 'action.hover', color: 'text.disabled' },
          }}
        />
      ))}
    </Box>
  );
};

export default memo(CodeInput);
